import { c as _c } from "react/compiler-runtime";
import { Maybe, PageEntityResponse, UploadFileEntityResponse } from "@lib/generated/graphql";
import clsx from "clsx";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import Button from "../../../../components/blocks/Button";
import AccordionHeader from "./AccordionHeader";
import AccordionImage from "./AccordionImage";
import AccordionItemContainer from "./AccordionItemContainer";
const isElementVisible = (target: HTMLDivElement | null) => {
  if (!target) {
    return false;
  }
  const targetPosition = {
    top: window.scrollY + target.getBoundingClientRect().top,
    bottom: window.scrollY + target.getBoundingClientRect().bottom
  };
  const windowPosition = {
    top: window.scrollY,
    bottom: window.scrollY + document.documentElement.clientHeight
  };
  return targetPosition.bottom > windowPosition.top && targetPosition.top < windowPosition.bottom;
};
export interface AccordionItem {
  id: string;
  image?: Maybe<UploadFileEntityResponse>;
  title: Maybe<string>;
  body?: Maybe<string>;
  href?: Maybe<string>;
  label?: Maybe<string>;
  trackingId?: Maybe<string>;
  page?: Maybe<PageEntityResponse>;
}
const Accordion: FC<{
  title?: Maybe<string>;
  subtitle?: Maybe<string>;
  autoOpenInterval?: Maybe<number>;
  className?: string;
  inverted?: boolean;
  serifClass?: string;
  items: Maybe<AccordionItem>[];
}> = t0 => {
  const $ = _c(47);
  const {
    className,
    title,
    subtitle,
    autoOpenInterval,
    inverted,
    serifClass,
    items
  } = t0;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isTimerPaused, setIsTimerPaused] = useState(false);
  const isTimerActive = !!autoOpenInterval && isVisible && !isTimerPaused;
  const image = items[activeIndex]?.image;
  const ref = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsTimerPaused(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleStartTimer = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsTimerPaused(true);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleStopTimer = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = index => {
      handleStopTimer();
      setActiveIndex(index);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleClickItem = t3;
  let t4;
  let t5;
  if ($[3] !== autoOpenInterval || $[4] !== isVisible) {
    t4 = () => {
      const handleScroll = () => {
        if (!autoOpenInterval) {
          return;
        }
        const newValue = isElementVisible(ref.current);
        if (isVisible && !newValue) {
          setIsVisible(false);
        } else {
          if (!isVisible && newValue) {
            setIsVisible(true);
            setIsTimerPaused(false);
          }
        }
      };
      window.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t5 = [isVisible, setIsVisible, autoOpenInterval];
    $[3] = autoOpenInterval;
    $[4] = isVisible;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== autoOpenInterval || $[8] !== isTimerActive || $[9] !== items.length) {
    t6 = () => {
      if (!isTimerActive || !autoOpenInterval) {
        return;
      }
      const timer = window.setInterval(() => {
        setActiveIndex(index_0 => (index_0 + 1) % items.length);
      }, autoOpenInterval);
      return () => clearInterval(timer);
    };
    $[7] = autoOpenInterval;
    $[8] = isTimerActive;
    $[9] = items.length;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== autoOpenInterval || $[12] !== isTimerActive || $[13] !== items) {
    t7 = [isTimerActive, autoOpenInterval, items];
    $[11] = autoOpenInterval;
    $[12] = isTimerActive;
    $[13] = items;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== className || $[16] !== inverted) {
    t8 = clsx("m:grid grid-cols-12 gap items-center", {
      "text-neutral-0": inverted
    }, className);
    $[15] = className;
    $[16] = inverted;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== inverted || $[19] !== serifClass || $[20] !== subtitle || $[21] !== title) {
    t9 = <AccordionHeader serifClass={serifClass} inverted={inverted} title={title} subtitle={subtitle} />;
    $[18] = inverted;
    $[19] = serifClass;
    $[20] = subtitle;
    $[21] = title;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== activeIndex || $[24] !== autoOpenInterval || $[25] !== inverted || $[26] !== isTimerActive || $[27] !== items) {
    let t11;
    if ($[29] !== activeIndex || $[30] !== autoOpenInterval || $[31] !== inverted || $[32] !== isTimerActive) {
      t11 = (item, index_1) => item && <AccordionItemContainer autoOpenInterval={autoOpenInterval} isTimerActive={isTimerActive} onClick={() => handleClickItem(index_1)} key={item.title} inverted={inverted} {...item} isActive={index_1 === activeIndex}>{item.body}{(item.href || item.page) && item.label && <Button id={item.id} trackingId={item.trackingId} size="md" variant={inverted ? "ghost_inverted" : "ghost"} paddingX={false} iconEnd="Right" href={item.href} page={item.page} className="mt-2s" label={item.label} />}</AccordionItemContainer>;
      $[29] = activeIndex;
      $[30] = autoOpenInterval;
      $[31] = inverted;
      $[32] = isTimerActive;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    t10 = items.map(t11);
    $[23] = activeIndex;
    $[24] = autoOpenInterval;
    $[25] = inverted;
    $[26] = isTimerActive;
    $[27] = items;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[34] !== t10) {
    t11 = <div onMouseEnter={handleStopTimer} onMouseLeave={handleStartTimer}>{t10}</div>;
    $[34] = t10;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t11 || $[37] !== t9) {
    t12 = <div className="m:col-span-6 l:col-span-5">{t9}{t11}</div>;
    $[36] = t11;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== image) {
    t13 = image && <AccordionImage image={image} />;
    $[39] = image;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t13) {
    t14 = <div className="col-span-6 l:col-start-7 hidden m:block h-full">{t13}</div>;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t12 || $[44] !== t14 || $[45] !== t8) {
    t15 = <div className={t8} ref={ref}>{t12}{t14}</div>;
    $[43] = t12;
    $[44] = t14;
    $[45] = t8;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  return t15;
};
export default Accordion;